import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatRelative } from "@/shared/format";
import { BottomSheet } from "@/shared/ui/BottomSheet";
import { fetchNotifications, markAllNotificationsRead, markNotificationRead } from "./api";
import type { AssistantNotification } from "./types";

const POLL_MS = 60_000;

function BellIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
      <path d="M13.7 20a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

function SeverityDot({ severity }: { severity: string }) {
  // "warning" e' fuori dal vocabolario (`Severity` in assistant/models.py) ma
  // resta nelle righe vecchie: fornitori e AllService l'hanno scritto per mesi.
  const color =
    severity === "error"
      ? "bg-red"
      : severity === "warn" || severity === "warning"
        ? "bg-amber"
        : severity === "info"
          ? "bg-blue"
          : "bg-ink-4";
  return <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${color}`} aria-hidden="true" />;
}

/**
 * Campanella notifiche flottante (bottom-right, sopra il VersionBadge z-[9999]):
 * bottone circolare col badge dei non letti + pannello con l'elenco e la
 * navigazione ai link.
 *
 * Si chiamava «Assistente OM» e aveva due tab: la chat AI e' stata rimossa il
 * 2026-09-02 (Davide: «per ora e' inutile»). Il file e il componente tengono il
 * nome vecchio di proposito — rinominarli sposterebbe import e cartella senza
 * cambiare nulla di cio' che si vede — ma le NOTIFICHE restano, e sono
 * infrastruttura condivisa: ci scrivono fatturazione (scadenze SdI,
 * riconciliazione) e fornitori, non solo il check giornaliero.
 *
 * Montato in AppShell (area autenticata), non in App.tsx: cosi' non compare
 * sulla /login. Poll ogni 60s, in pausa quando la tab e' nascosta
 * (`document.hidden`) — nessuno spreco di chiamate a finestra in background.
 */
export function AssistantWidget({ variant = "fab" }: { variant?: "fab" | "bar" } = {}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AssistantNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Il "segna letto" è ottimistico: senza rollback un fallimento restava muto e
  // il badge resuscitava da solo al poll successivo, come se fosse arrivata una
  // notifica nuova.
  const [notifError, setNotifError] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);

  // Poll notifiche: un'unica GET (read+unread) da cui deriviamo lista e conteggio.
  // Il gate document.hidden vale SOLO per il poll periodico: il primo load al
  // mount va sempre fatto (con finestra occlusa/minimizzata il gate lasciava
  // badge e lista vuoti finche' il poll non riusciva — visto in collaudo).
  const loadNotifications = useCallback(async (force = false): Promise<void> => {
    if (!force && document.hidden) return;
    try {
      const rows = await fetchNotifications(false, 50);
      setNotifications(rows);
      setUnreadCount(rows.filter((n) => !n.read_at).length);
    } catch {
      // Best-effort: il badge resta all'ultimo valore noto (mai toast ricorrenti).
    }
  }, []);

  useEffect(() => {
    // ``setTimeout`` a catena, non ``setInterval``: il giro riparte DOPO la
    // risposta. Il 2026-09-08 questo endpoint rispondeva in 47 s di media
    // contro un timer di 60, e ogni richiesta appesa trattiene una connessione
    // del pool. Stesso schema di SyncStatusPill e HaltedLanesAlert.
    let timer: number | null = null;
    let attivo = true;
    const giro = async (): Promise<void> => {
      await loadNotifications();
      if (attivo) timer = window.setTimeout(() => void giro(), POLL_MS);
    };
    void loadNotifications(true).then(() => {
      if (attivo) timer = window.setTimeout(() => void giro(), POLL_MS);
    });
    const onVisible = (): void => {
      if (!document.hidden) void loadNotifications();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      attivo = false;
      if (timer !== null) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadNotifications]);

  // Apertura pannello: refresh immediato cosi' l'elenco e' sempre fresco.
  useEffect(() => {
    if (open) void loadNotifications(true);
  }, [open, loadNotifications]);

  // Escape chiude + focus trap leggero: alla apertura il focus entra nel pannello.
  // Senza la textarea della chat, il pannello stesso e' l'unico bersaglio.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
  }, [open]);

  async function openNotification(n: AssistantNotification): Promise<void> {
    if (!n.read_at) {
      const now = new Date().toISOString();
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: now } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await markNotificationRead(n.id);
      } catch {
        // Best-effort: l'ottimistico resta; il prossimo poll riallinea se serve.
      }
    }
    if (n.link) {
      setOpen(false);
      void navigate(n.link);
    }
  }

  async function markAll(): Promise<void> {
    const now = new Date().toISOString();
    const prevNotifiche = notifications;
    const prevNonLette = unreadCount;
    setNotifications((prev) => prev.map((x) => (x.read_at ? x : { ...x, read_at: now })));
    setUnreadCount(0);
    setNotifError(null);
    try {
      await markAllNotificationsRead();
    } catch {
      setNotifications(prevNotifiche);
      setUnreadCount(prevNonLette);
      setNotifError("Non sono riuscito a segnarle come lette. Riprova.");
    }
  }

  const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);
  const ariaLabel = open
    ? "Chiudi notifiche"
    : unreadCount > 0
      ? `Notifiche, ${unreadCount} non lette`
      : "Notifiche";

  const corpo = (
    <div className="flex min-h-0 flex-1 flex-col">
      {notifError && (
        <div role="alert" className="border-b border-line px-3 py-2 text-[12px] text-red">
          {notifError}
        </div>
      )}
      {notifications.length > 0 && (
        <div className="flex justify-end border-b border-line px-3 py-1.5 max-md:px-3 max-md:py-0">
          <button
            type="button"
            onClick={() => void markAll()}
            disabled={unreadCount === 0}
            className="text-[11.5px] font-medium text-accent hover:underline disabled:text-ink-4 disabled:no-underline max-md:min-h-11 max-md:px-2 max-md:text-[14px]"
          >
            Segna tutte lette
          </button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <p className="px-4 py-8 text-center text-[12.5px] text-ink-3 max-md:text-[15px] max-md:text-ink">
            Nessuna notifica
          </p>
        ) : (
          <ul>
            {notifications.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => void openNotification(n)}
                  className={`flex w-full gap-2.5 border-b border-line px-3 py-2.5 text-left hover:bg-paper max-md:gap-3 max-md:px-5 max-md:py-3.5 max-md:active:bg-paper-2 ${
                    n.read_at ? "opacity-60" : ""
                  }`}
                >
                  <SeverityDot severity={n.severity} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[12.5px] font-medium text-ink max-md:whitespace-normal max-md:text-[15px] max-md:font-semibold">
                        {n.title}
                      </span>
                      <span className="shrink-0 text-[11px] text-ink-4 max-md:text-[12.5px] max-md:text-ink">
                        {formatRelative(n.created_at)}
                      </span>
                    </div>
                    {n.body && (
                      <p className="mt-0.5 line-clamp-2 text-[11.5px] text-ink-3 max-md:line-clamp-3 max-md:text-[14px] max-md:text-ink">
                        {n.body}
                      </p>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  // Telefono (redesign 2026-10-23): la campanella sta nella barra in alto, non
  // galleggia sopra la lista coprendo i tasti delle card, e l'elenco si apre in
  // un foglio dal basso a tutta larghezza.
  if (variant === "bar") {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="dialog"
          className="om-press relative grid h-11 w-11 place-items-center rounded-full text-ink active:bg-paper-2"
        >
          <BellIcon className="h-[22px] w-[22px]" />
          {unreadCount > 0 && (
            <span className="absolute right-1 top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red px-1 text-[10px] font-bold text-white ring-2 ring-paper">
              {badgeLabel}
            </span>
          )}
        </button>
        <BottomSheet
          open={open}
          onClose={() => setOpen(false)}
          title={unreadCount > 0 ? `Notifiche (${badgeLabel})` : "Notifiche"}
        >
          <div className="-mx-5">{corpo}</div>
        </BottomSheet>
      </>
    );
  }

  return (
    // Mobile: il FAB sale sopra la BottomNav (bottom-20) e il pannello diventa
    // un bottom-sheet quasi a tutta larghezza (left/right-2). Desktop invariato.
    <div className="fixed bottom-16 right-4 z-[9998] flex flex-col items-end gap-2 max-md:bottom-20 max-md:left-2 max-md:right-2">
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notifiche"
          tabIndex={-1}
          className="flex max-h-[70vh] w-[380px] flex-col overflow-hidden rounded-lg border border-line bg-card shadow-popover outline-none max-md:w-full max-md:max-h-[75dvh]"
        >
          <header className="flex items-center justify-between border-b border-line px-4 py-3">
            <div className="flex items-center gap-2">
              <BellIcon className="h-[18px] w-[18px] text-accent" />
              <span className="font-display text-[15px] font-bold text-ink">
                Notifiche{unreadCount > 0 ? ` (${badgeLabel})` : ""}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Chiudi"
              className="rounded-sm p-1 text-ink-3 hover:bg-paper hover:text-ink"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </header>

          {corpo}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="relative flex h-12 w-12 items-center justify-center rounded-full bg-accent-2 text-white shadow-popover hover:brightness-95"
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red px-1 text-[10px] font-bold text-white">
            {badgeLabel}
          </span>
        )}
      </button>
    </div>
  );
}
